'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Section {
  id: string;
  label: string;
  scrollOffset?: number;
}

interface SectionNavigatorProps {
  sections?: Section[];
  onSectionChange?: (sectionId: string) => void;
}

const SectionNavigator = ({ 
  sections = [
    { id: 'hero', label: 'Home' },
    { id: 'how-it-works', label: 'How It Works' },
    { id: 'benefits', label: 'Benefits' },
    { id: 'testimonials', label: 'Testimonials' },
    { id: 'get-access', label: 'Get Access' }
  ],
  onSectionChange
}: SectionNavigatorProps) => {
  const [activeSection, setActiveSection] = useState('hero');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          setActiveSection(sectionId);
          if (onSectionChange) {
            onSectionChange(sectionId);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [sections, onSectionChange]);

  const scrollToSection = (sectionId: string, offset: number = 80) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Desktop Navigation Dots */}
      <div data-component-id="src/components/common/SectionNavigator.tsx:78:6" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="78" data-component-file="SectionNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20lg%3Aflex%20fixed%20right-8%20top-1%2F2%20-translate-y-1%2F2%20z-50%20flex-col%20space-y-3%22%7D" className="hidden lg:flex fixed right-8 top-1/2 -translate-y-1/2 z-50 flex-col space-y-3">
        {sections.map((section) => (
          <button data-component-id="src/components/common/SectionNavigator.tsx:80:10" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="80" data-component-file="SectionNavigator.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22group%20relative%22%7D"
            key={section.id}
            onClick={() => scrollToSection(section.id, section.scrollOffset)}
            className="group relative"
            aria-label={`Navigate to ${section.label}`}
          >
            <div data-component-id="src/components/common/SectionNavigator.tsx:86:12" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="86" data-component-file="SectionNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              className={`w-3 h-3 rounded-full transition-smooth ${
                activeSection === section.id
                  ? 'bg-secondary scale-125' :'bg-border hover:bg-secondary/50'
              }`}
            />
            <span data-component-id="src/components/common/SectionNavigator.tsx:92:12" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="92" data-component-file="SectionNavigator.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20right-6%20top-1%2F2%20-translate-y-1%2F2%20px-3%20py-1.5%20bg-primary%20text-primary-foreground%20text-sm%20font-medium%20rounded-lg%20opacity-0%20group-hover%3Aopacity-100%20transition-smooth%20whitespace-nowrap%20pointer-events-none%22%7D" className="absolute right-6 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-primary text-primary-foreground text-sm font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-smooth whitespace-nowrap pointer-events-none">
              {section.label}
            </span>
          </button>
        ))}
      </div>

      {/* Mobile Hamburger Menu */}
      <div data-component-id="src/components/common/SectionNavigator.tsx:100:6" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="100" data-component-file="SectionNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Ahidden%20fixed%20right-5%20top-24%20z-50%22%7D" className="lg:hidden fixed right-5 top-24 z-50">
        <button data-component-id="src/components/common/SectionNavigator.tsx:101:8" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="101" data-component-file="SectionNavigator.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-12%20h-12%20rounded-full%20bg-primary%20text-primary-foreground%20shadow-elevated%20flex%20items-center%20justify-center%20transition-smooth%20hover%3Ascale-105%22%7D"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-elevated flex items-center justify-center transition-smooth hover:scale-105"
          aria-label="Toggle navigation menu"
        >
          <Icon data-component-id="src/components/common/SectionNavigator.tsx:106:10" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="106" data-component-file="SectionNavigator.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" 
            name={isMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} 
            size={24} 
            variant="outline"
          />
        </button>

        {isMenuOpen && (
          <div data-component-id="src/components/common/SectionNavigator.tsx:114:10" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="114" data-component-file="SectionNavigator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20right-0%20top-14%20bg-background%20rounded-lg%20shadow-elevated%20border%20border-border%20min-w-%5B200px%5D%20animate-scale-in%22%7D" className="absolute right-0 top-14 bg-background rounded-lg shadow-elevated border border-border min-w-[200px] animate-scale-in">
            <nav data-component-id="src/components/common/SectionNavigator.tsx:115:12" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="115" data-component-file="SectionNavigator.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22py-2%22%7D" className="py-2">
              {sections.map((section) => (
                <button data-component-id="src/components/common/SectionNavigator.tsx:117:16" data-component-path="src/components/common/SectionNavigator.tsx" data-component-line="117" data-component-file="SectionNavigator.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                  key={section.id}
                  onClick={() => scrollToSection(section.id, section.scrollOffset)}
                  className={`w-full text-left px-4 py-3 text-sm font-medium transition-smooth ${
                    activeSection === section.id
                      ? 'text-secondary bg-secondary/10' :'text-text-secondary hover:text-primary hover:bg-muted'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>
    </>
  );
};

export default SectionNavigator;